import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import type {
  VendorContact,
  NegotiationMode,
  VendorTier,
  ExclusionCategory,
} from "@/types/negotiations";

const QUERY_KEY = ["vendor_contacts"] as const;

async function fetchContacts(): Promise<VendorContact[]> {
  const { data, error } = await supabase
    .from("vendor_contacts")
    .select("*")
    .order("vendor_name", { ascending: true });

  if (error) throw error;
  return (data ?? []) as VendorContact[];
}

/** Upsert a vendor_contacts row keyed on vendor_name (company_id is set by RLS/default). */
async function upsertContact(
  vendor_name: string,
  updates: Record<string, unknown>
): Promise<VendorContact> {
  const { data, error } = await supabase
    .from("vendor_contacts")
    .upsert(
      { vendor_name, ...updates, updated_at: new Date().toISOString() },
      { onConflict: "company_id,vendor_name" }
    )
    .select()
    .single();

  if (error) throw error;
  return data as VendorContact;
}

export function useVendorContacts() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const {
    data: contacts = [],
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchContacts,
  });

  // ── helpers ──────────────────────────────────────────────────────────────

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: QUERY_KEY });
  }

  // ── setMode ──────────────────────────────────────────────────────────────

  const setModeMutation = useMutation({
    mutationFn: async ({
      vendor_name,
      mode,
      userId,
    }: {
      vendor_name: string;
      mode: NegotiationMode;
      userId?: string;
    }) => {
      const extra =
        mode === "auto"
          ? {
              auto_enabled_by: userId ?? null,
              auto_enabled_at: new Date().toISOString(),
            }
          : {};
      return upsertContact(vendor_name, { negotiation_mode: mode, ...extra });
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (err) => {
      toast({
        title: "Failed to update mode",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  // ── setExcluded ───────────────────────────────────────────────────────────

  const setExcludedMutation = useMutation({
    mutationFn: async ({
      vendor_name,
      excluded,
      reason,
      category,
      userId,
    }: {
      vendor_name: string;
      excluded: boolean;
      reason?: string | null;
      category?: ExclusionCategory | null;
      userId?: string;
    }) => {
      const extra = excluded
        ? {
            excluded_by: userId ?? null,
            excluded_at: new Date().toISOString(),
            exclusion_reason: reason ?? null,
            exclusion_category: category ?? null,
          }
        : {
            excluded_by: null,
            excluded_at: null,
            exclusion_reason: null,
            exclusion_category: null,
          };
      return upsertContact(vendor_name, { excluded, ...extra });
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (err) => {
      toast({
        title: "Failed to update exclusion",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  // ── setTone ───────────────────────────────────────────────────────────────

  const setToneMutation = useMutation({
    mutationFn: async ({ vendor_name, tone }: { vendor_name: string; tone: string | null }) => {
      return upsertContact(vendor_name, { tone });
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (err) => {
      toast({
        title: "Failed to update tone",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  // ── setTier ───────────────────────────────────────────────────────────────

  const setTierMutation = useMutation({
    mutationFn: async ({ vendor_name, tier }: { vendor_name: string; tier: VendorTier }) => {
      return upsertContact(vendor_name, { tier });
    },
    onSuccess: () => {
      invalidate();
    },
    onError: (err) => {
      toast({
        title: "Failed to update tier",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  // ── upsertContact (full record) ───────────────────────────────────────────

  const upsertMutation = useMutation({
    mutationFn: async ({
      vendor_name,
      updates,
    }: {
      vendor_name: string;
      updates: Partial<Omit<VendorContact, "id" | "company_id" | "created_at" | "updated_at">>;
    }) => {
      return upsertContact(vendor_name, updates as Record<string, unknown>);
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Vendor contact saved" });
    },
    onError: (err) => {
      toast({
        title: "Failed to save contact",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  // ── deleteContact ─────────────────────────────────────────────────────────

  const deleteMutation = useMutation({
    mutationFn: async (vendor_name: string) => {
      const { error } = await supabase
        .from("vendor_contacts")
        .delete()
        .eq("vendor_name", vendor_name);
      if (error) throw error;
    },
    onSuccess: () => {
      invalidate();
      toast({ title: "Vendor contact removed" });
    },
    onError: (err) => {
      toast({
        title: "Failed to remove contact",
        description: (err as Error).message,
        variant: "destructive",
      });
    },
  });

  return {
    contacts,
    isLoading,
    error,
    refetch,

    /** Set the negotiation mode; stamps auto_enabled_by/at when switching to 'auto'. */
    setMode: (vendor_name: string, mode: NegotiationMode, userId?: string) =>
      setModeMutation.mutateAsync({ vendor_name, mode, userId }),

    /** Set excluded flag; stamps excluded_by/at + reason/category when excluding. */
    setExcluded: (
      vendor_name: string,
      excluded: boolean,
      opts?: { reason?: string | null; category?: ExclusionCategory | null; userId?: string }
    ) =>
      setExcludedMutation.mutateAsync({
        vendor_name,
        excluded,
        reason: opts?.reason,
        category: opts?.category,
        userId: opts?.userId,
      }),

    /** Set per-vendor tone override. */
    setTone: (vendor_name: string, tone: string | null) =>
      setToneMutation.mutateAsync({ vendor_name, tone }),

    /** Set vendor tier (strategic / standard / transactional). */
    setTier: (vendor_name: string, tier: VendorTier) =>
      setTierMutation.mutateAsync({ vendor_name, tier }),

    /** Upsert the full vendor contact record. */
    upsertContact: (
      vendor_name: string,
      updates: Partial<Omit<VendorContact, "id" | "company_id" | "created_at" | "updated_at">>
    ) => upsertMutation.mutateAsync({ vendor_name, updates }),

    /** Delete a vendor contact by vendor_name. */
    deleteContact: (vendor_name: string) => deleteMutation.mutateAsync(vendor_name),

    isSettingMode: setModeMutation.isPending,
    isSettingExcluded: setExcludedMutation.isPending,
    isSettingTone: setToneMutation.isPending,
    isSettingTier: setTierMutation.isPending,
    isUpserting: upsertMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
}
